import { getScreenSize, Screens } from 'config/app-constants';
import { DataInfoType, DatalistDataSourceType, DataSourceRequestType, DatalistTablePropsType, DatalistOnClickType } from 'types/datalist-types';
import CoreComponent from 'utilities/component-utilities/CoreComponent';
import { resolveFilterInput } from 'utilities/datalist/datalist-filter-utilities';
import { resolveSort } from 'utilities/datalist/datalist-sort-utilities';
import './Datalist.scss';
import DatalistCard from './partials/DatalistCard';
import DatalistHeader, { DatalistHeaderButtonsType } from './partials/DatalistHeader';
import DatalistPagination from './partials/DatalistPagination';
import DatalistFilter, { FILTER_QUERY_PARAM } from './partials/filter/DatalistFilter';
import DatalistSort from './partials/sort/DatalistSort';
import DatalistTable from './partials/table/DatalistTable';
import * as lodash from 'lodash';
import If from '../If';
import StringUtilities from "../../../utilities/common-utilities/string-utilities";

const PAGE_SIZE = 50;

// #region types

type DatalistPagePropsType = 'off' | {
    pageSize?: number,
    loadOnScroll?: boolean
}

export type DatalistPropsType<TItemType, TFilterType, TSortType> = {
    name: string
    itemId: (i: Exclude<DataInfoType<TItemType>['items'], null | undefined>[0]) => string | number
    dataSource: DatalistDataSourceType<TItemType, TFilterType, TSortType>,
    table: DatalistTablePropsType<TItemType, TFilterType, TSortType>,
    page?: DatalistPagePropsType,
    onItemClick?: DatalistOnClickType<TItemType>
    defaultView?: 'card' | 'table' | 'only-table' | 'only-card',
    header?: false | {
        rightButtons?: DatalistHeaderButtonsType[],
        hideRefreshButton?: boolean
        hideSearchbox?: boolean
    }
    events?: {
        onRefresh?: (() => void) | (() => Promise<void>),
        onReload?: (() => void) | (() => Promise<void>)
    }
}

type DatalistState<TItemType> = {
    listInfo: {
        take?: number,
        skip?: number,
        searchText?: string,
        lastUpdated: Date,
    },
    data: DataInfoType<TItemType>,
    loading: boolean,
    view: 'table' | 'card' | 'only-table' | 'only-card'
}
// #endregion types

const getFilterQueryParam = (param: string) => {
    return new URLSearchParams(window.location.search).get(param) || '';
}

const getDefaultView = (defaultView?: 'table' | 'card' | 'only-table' | 'only-card') => {
    switch (defaultView) {
        case 'only-table':
            return 'table';
        case 'only-card':
            return 'card';
        default:
            return defaultView;
    }
}

class Datalist<TItemType, TFilterType, TSortType> extends CoreComponent<DatalistPropsType<TItemType, TFilterType, TSortType>, DatalistState<TItemType>> {
    constructor(props: DatalistPropsType<TItemType, TFilterType, TSortType>) {
        super(props);
        this.state = {
            listInfo: {
                lastUpdated: new Date(),
            },
            view: getDefaultView(props.defaultView) || ((getScreenSize() === Screens.sm) ? 'card' : 'table'),
            data: {
                items: [],
                count: 0
            },
            loading: 'data' in props.dataSource ? false : true
        }
    }

    // #region init
    componentDidMount(): void {
        const pageSize = this.getPageSize(this.props.page);
        const listInfo: DatalistState<TItemType>['listInfo'] = {
            take: pageSize,
            skip: pageSize ? 0 : undefined,
            lastUpdated: new Date(),
        }

        this.setState({ listInfo })
    }

    componentDidUpdate(_prevProps: Readonly<DatalistPropsType<TItemType, TFilterType, TSortType>>, prevState: Readonly<DatalistState<TItemType>>, _snapshot?: any): void {
        if (prevState.listInfo.lastUpdated !== this.state.listInfo.lastUpdated) {
            this.loadData().then();
        }
    }

    // #endregion init

    // #region public methods
    reload = async () => {
        await this.loadData();

        if (this.props.events?.onReload) {
            const a = this.props.events.onReload();
            if (typeof a == "object") {
                a.then();
            }
        }
    }

    refresh = () => {
        this.setState({
            data: {
                ...this.state.data
            }
        });

        if (this.props.events?.onRefresh) {
            const a = this.props.events.onRefresh();
            if (typeof a == "object") {
                a.then();
            }
        }
    }

    data = () => {
        return this.state.data?.items || [];
    }
    // #endregion public methods

    // #region renders
    private renderView = () => {
        if (typeof this.props.table.render === 'function') {
            const viewIsTable = this.state.view === 'table';
            return (
                <div className={`table-wrapper ${viewIsTable && 'overflow-y-auto'}`}>
                    {this.props.table.render(this.data())}
                </div>
            );
        }

        const props = {
            columns: this.props.table.columns,
            ...(this.props.table.render || {}),
            render: this.props.table.render,
            itemId: this.props.itemId,
            items: this.data(),
            onItemClick: this.props.onItemClick,
            loading: this.state.loading,
            name: this.props.name,
            onDelete: this.reload
        }

        const viewIsTable = this.state.view === 'table';
        return (
            <div className={`table-wrapper ${viewIsTable && 'overflow-y-auto'}`}>
                {viewIsTable ? <DatalistTable {...props} /> : <DatalistCard {...props} />}
            </div>
        );
    }

    changeView = () => {
        this.setState({
            view: this.state.view === 'table' ? 'card' : 'table'
        });
    }

    render() {
        return (
            <div className='app-datalist'>
                <If condition={this.props.header !== false}>
                    <DatalistHeader
                        onSearch={searchText => this.setListInfo({ searchText, skip: 0 })}
                        name={this.props.name}
                        hideRefreshButton={!!(this.props.header && 'hideRefreshButton' in this.props.header && this.props.header.hideRefreshButton)}
                        hideSearchbox={!!(this.props.header && 'hideSearchbox' in this.props.header && this.props.header.hideSearchbox)}
                        showChangeView={!(this.props.defaultView && this.props.defaultView.startsWith('only'))}
                        changeView={this.changeView}
                        hasSort={this.props.table.columns.some(c => c.sort)}
                        hasFilter={this.props.table.columns.some(c => c.filter)}
                        onReload={this.reload}
                        rightButtons={typeof this.props.header === 'object' ? this.props.header?.rightButtons : undefined}
                    />
                </If>
                {this.renderView()}
                <footer>
                    <DatalistPagination
                    skip={this.state.listInfo.skip}
                    take={this.state.listInfo.take}
                    totalCount={this.state.data?.count || 0}
                    onPageChange={skip => this.setListInfo({ skip })}
                    name={this.props.name}
                />
                </footer>
                <DatalistFilter
                    columns={this.props.table.columns.filter(x => x.filter)}
                    name={this.props.name}
                    filters={'filter' in this.props.dataSource ? this.props.dataSource.filter : undefined}
                    triggerChange={this.reload}
                />
                <DatalistSort
                    columns={this.props.table.columns.filter(x => x.sort)}
                    name={this.props.name}
                    triggerChange={this.reload}
                    sort={'sort' in this.props.dataSource ? this.props.dataSource.sort : undefined}
                />
            </div>
        );
    }
    // #endregion renders

    // #region private helpers

    // #region resolvers
    private loadData = async (options?: DataSourceRequestType<TFilterType, TSortType>) => {
        const opt = {
            take: this.state.listInfo.take,
            skip: this.state.listInfo.skip,
            searchText: this.state.listInfo.searchText,
            sort: this.resolveSorter() as TSortType[],
            filters: resolveFilterInput(getFilterQueryParam(`${this.props.name}-${FILTER_QUERY_PARAM}`), this.props.table.columns, 'filter' in this.props.dataSource ? lodash.clone(this.props.dataSource.filter) : undefined),
            ...options
        }

        try {
            this.setState({
                loading: true
            })
            const data = await this.resolveData(this.props.dataSource, opt);
            this.setState({
                data,
                loading: false
            })
        } catch (error) {
            this.setState({
                loading: false,
                data: {
                    count: 0,
                    items: []
                }
            });
            this.context.notify('error', 'error.loading-data');
            throw error;
        }
    }

    private resolveSorter = () => {
        let sort = 'sort' in this.props.dataSource && this.props.dataSource.sort ? lodash.clone(this.props.dataSource.sort) : undefined;
        const sortString = getFilterQueryParam(`${this.props.name}-sort`);
        if (sortString) {
            sort = StringUtilities.parseValue(sortString);
        }
        if (!sort) return [];
        return sort.map(s => {
            const column = this.props.table.columns.find(c => c.name === s.name);
            if (!column?.sort || !s.direction) return undefined;
            if (Array.isArray(column.sort)) {
                const sortArray = column.sort;
                let finalSort: any = {};
                let sortObj: any = finalSort;
                column.sort.forEach((sortField, index) => {
                    if (index === sortArray.length - 1) {
                        sortObj[sortField] = s.direction;
                    } else {
                        sortObj[sortField] = {};
                        sortObj = sortObj[sortField];
                    }
                });
                return finalSort;
            } else if (typeof column.sort === 'boolean') {
                return resolveSort<TSortType>(column.name, s.direction);
            } else {
                return column.sort.resolve(s.direction);
            }
        }).filter(x => x !== undefined);
    }

    private resolveData = async <TItemType, TFilterType, TSortType>(dataSource: DatalistDataSourceType<Exclude<DataInfoType<TItemType>['items'], null | undefined>[0], TFilterType, TSortType>, options: DataSourceRequestType<TFilterType, TSortType>): Promise<DataInfoType<TItemType>> => {
        if ('data' in dataSource && Array.isArray(dataSource.data)) {
            return {
                items: options.take ? [...dataSource.data].slice(options.skip || 0, options.take) : dataSource.data,
                count: dataSource.data.length
            }
        }

        if ('data' in dataSource && typeof dataSource.data === 'function') {
            const dataFunc = dataSource.data(options);
            let data: TItemType[];
            if (Array.isArray(dataFunc)) {
                data = dataFunc
            } else {
                data = await dataFunc;
            }

            return {
                items: options.take ? data.slice(options.skip || 0, options.take) : data,
                count: dataSource.data.length
            }
        }

        if ('request' in dataSource) {
            const data = await dataSource.request(options);
            return data;
        }

        return {
            items: [],
            count: 0
        };
    }

    private getPageSize = (page?: DatalistPagePropsType): number | undefined => {
        if (page === 'off') return undefined;
        return page?.pageSize || PAGE_SIZE;
    }
    // #endregion resolvers

    // #region change handlers
    private setListInfo = (listInfo: Partial<DatalistState<TItemType>['listInfo']>) => {
        this.setState({
            listInfo: {
                ...this.state.listInfo,
                ...listInfo,
                lastUpdated: new Date()
            }
        })
    }
    // #endregion change handlers
    // #endregion private helpers
}

export default Datalist;
